// System-administration console contracts (PD-003). Served only on configured platform hosts.
import { z } from 'zod';

export const PLATFORM_PREFIX = '/api/platform/v1';

const name = z.string().trim().min(1, 'v.required').max(200, 'v.tooLong');
export const Hostname = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/, 'v.hostname')
  .max(253, 'v.tooLong');
export const Slug = z.string().trim().toLowerCase().regex(/^[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?$/, 'v.slug');

export const PlatformLoginRequest = z.object({ email: z.string().trim().min(3).max(254), password: z.string().min(1).max(1024) });

export interface PlatformSessionResponse {
  readonly account: { readonly id: string; readonly email: string; readonly displayName: string; readonly locale: 'en' | 'ar' };
  readonly csrfToken: string;
  readonly expiresAt: string;
  readonly mfa: { readonly enrolled: boolean; readonly required: boolean };
  /** PD-714 / PD-729: present and true only in demo mode (IS_DEMO). */
  readonly demo?: true;
}

export const CreateTenantRequest = z.object({
  slug: Slug,
  name,
  hostname: Hostname,
  multiSite: z.boolean().default(false),
  maxActiveSites: z.number().int().min(1, 'v.range').max(10000, 'v.range').nullable().default(null),
  firstAdmin: z.object({ email: z.email('v.email').max(254, 'v.tooLong'), displayName: name }),
});
export type CreateTenantRequest = z.infer<typeof CreateTenantRequest>;

export const UpdateTenantRequest = z.object({
  expectedVersion: z.number().int().min(1),
  name: name.optional(),
  status: z.enum(['active', 'suspended']).optional(),
  maxActiveSites: z.number().int().min(1, 'v.range').max(10000, 'v.range').nullable().optional(),
  entitlements: z.array(z.string().regex(/^[a-z][a-z_]{1,63}$/)).max(50).optional(),
  reason: z.string().trim().min(1, 'v.required').max(500, 'v.tooLong'),
});
export type UpdateTenantRequest = z.infer<typeof UpdateTenantRequest>;

export const AddDomainRequest = z.object({ hostname: Hostname });

export const CreateTenantAdminRequest = z.object({ email: z.email('v.email').max(254, 'v.tooLong'), displayName: name });

export interface TenantSummary {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly status: 'active' | 'suspended';
  readonly version: number;
  readonly hostnames: readonly string[];
  readonly entitlements: readonly string[];
  readonly maxActiveSites: number | null;
  readonly activeSites: number;
  readonly policyOverride: boolean;
}

export interface TenantDetail extends TenantSummary {
  readonly administrators: ReadonlyArray<{ readonly id: string; readonly email: string; readonly displayName: string; readonly status: string; readonly grantId: string | null }>;
  readonly availableEntitlements: ReadonlyArray<{ readonly code: string; readonly description: string }>;
}

export interface TenantCreated {
  readonly tenant: TenantSummary;
  readonly firstAdmin: { readonly userId: string; readonly email: string };
  /** One-time secret shown to the system administrator; never stored in clear. */
  readonly activationToken: string;
  readonly activationExpiresAt: string;
  readonly signInUrlHost: string;
}

export interface PlatformAuditEvent {
  readonly id: string;
  readonly occurredAt: string;
  readonly actorAccountId: string | null;
  readonly tenantId: string | null;
  readonly action: string;
  readonly resourceType: string;
  readonly resourceId: string | null;
  readonly outcome: string;
  readonly reason: string | null;
  readonly changes: Record<string, unknown> | null;
  readonly correlationId: string | null;
}
